import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ExerciseCard = ({ 
  exercise, 
  index, 
  onUpdate, 
  onRemove, 
  onMove, 
  onSupersetCreate,
  phaseOptions,
  isInSuperset = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleConfigUpdate = (field, value) => {
    const updatedExercise = {
      ...exercise,
      planConfig: {
        ...exercise?.planConfig,
        [field]: value
      }
    };

    // Update starting intensity when phase changes
    if (field === 'selectedPhase') {
      updatedExercise.planConfig.startingIntensity = exercise?.phases?.[value]?.startingIntensity;
    }

    onUpdate(exercise?.id, updatedExercise);
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    e?.dataTransfer?.setData('text/plain', exercise?.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    const draggedId = e?.dataTransfer?.getData('text/plain');
    if (draggedId !== exercise?.id) {
      // Handle reordering logic here
      console.log('Reorder exercises:', draggedId, exercise?.id);
    }
  };

  const createSuperset = () => {
    const supersetId = `superset-${Date.now()}`;
    onSupersetCreate(exercise?.id, supersetId);
  };

  const getPhaseInfo = () => {
    const phase = exercise?.phases?.[exercise?.planConfig?.selectedPhase];
    return `${phase?.minReps}-${phase?.maxReps} reps`;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'text-success';
      case 'intermediate': return 'text-warning';
      case 'advanced': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div
      draggable={!isInSuperset}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`
        bg-surface border border-border rounded-lg transition-all duration-200 ease-out
        ${isDragging ? 'opacity-50 scale-95' : 'opacity-100 scale-100'}
        ${isInSuperset ? 'border-secondary/50' : 'hover:border-primary/50'}
        breathing-focus
      `}
    >
      {/* Exercise Header */}
      <div className="p-4">
        <div className="flex items-start space-x-4">
          {/* Exercise Image */}
          <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0">
            <Image
              src={exercise?.image}
              alt={exercise?.name}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Exercise Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-semibold text-foreground mb-1">{exercise?.name}</h3>
                <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                  <span>{exercise?.equipment}</span>
                  <span>•</span>
                  <span className={getDifficultyColor(exercise?.difficulty)}>
                    {exercise?.difficulty}
                  </span>
                  <span>•</span>
                  <span>{getPhaseInfo()}</span>
                </div>
              </div>
              
              {!isInSuperset && (
                <div className="flex items-center space-x-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="breathing-focus"
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemove(exercise?.id)}
                    className="breathing-focus text-error hover:text-error"
                    aria-label="Remove exercise"
                  >
                    <Icon name="X" size={16} />
                  </Button>
                  
                  <div className="cursor-grab active:cursor-grabbing p-1">
                    <Icon name="GripVertical" size={16} className="text-muted-foreground" />
                  </div>
                </div>
              )}
            </div>

            {/* Quick Config */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">Phase:</span>
                <Select
                  options={phaseOptions}
                  value={exercise?.planConfig?.selectedPhase}
                  onChange={(value) => handleConfigUpdate('selectedPhase', value)}
                  className="w-40"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">Intensity:</span>
                <Input
                  type="number"
                  value={exercise?.planConfig?.startingIntensity}
                  onChange={(e) => handleConfigUpdate('startingIntensity', parseInt(e?.target?.value) || 0)}
                  className="w-20"
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Muscle Groups */}
        <div className="flex flex-wrap gap-2 mt-3">
          {exercise?.muscleGroups?.map((muscle, idx) => (
            <span
              key={idx}
              className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-full"
            >
              {muscle}
            </span>
          ))}
        </div>
      </div>
      {/* Expanded Configuration */}
      {isExpanded && (
        <div className="border-t border-border p-4 bg-muted/30">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Superset Configuration */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Superset Configuration
              </label>
              {exercise?.planConfig?.supersetGroup ? (
                <div className="flex items-center space-x-2">
                  <span className="px-3 py-1 bg-secondary/10 text-secondary rounded-full text-sm">
                    {exercise?.planConfig?.supersetGroup}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    Order: {exercise?.planConfig?.supersetOrder}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleConfigUpdate('supersetGroup', null)}
                    iconName="X"
                    className="text-error hover:text-error"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={createSuperset}
                  iconName="Link"
                  className="breathing-focus"
                >
                  Create Superset
                </Button>
              )}
            </div>

            {/* Custom Adjustment */}
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={exercise?.planConfig?.customAdjustment}
                  onChange={(e) => handleConfigUpdate('customAdjustment', e?.target?.checked)}
                  className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                />
                <span className="text-sm text-foreground">Custom intensity adjustment</span>
              </label>
              <p className="text-xs text-muted-foreground mt-1">
                Override automatic intensity progression
              </p>
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <Input
                label="Trainer Notes"
                type="text"
                placeholder="Special instructions or modifications..."
                value={exercise?.planConfig?.notes}
                onChange={(e) => handleConfigUpdate('notes', e?.target?.value)}
              />
            </div>

            {/* Phase Details */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Phase Progression Details
              </label>
              <div className="grid grid-cols-3 gap-4 text-sm">
                {Object.entries(exercise?.phases)?.map(([phaseKey, phaseData]) => (
                  <div
                    key={phaseKey}
                    className={`
                      p-3 rounded-lg border
                      ${exercise?.planConfig?.selectedPhase === phaseKey
                        ? 'border-primary bg-primary/5' :'border-border bg-surface'
                      }
                    `}
                  >
                    <div className="font-medium text-foreground mb-1">
                      {phaseKey?.replace('-', ' ')?.toUpperCase()}
                    </div>
                    <div className="text-muted-foreground">
                      {phaseData?.minReps}-{phaseData?.maxReps} reps
                    </div>
                    <div className="text-muted-foreground">
                      Start: {phaseData?.startingIntensity}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseCard;